import { useState, useEffect } from 'react';
import { Container } from '@/components/layout/Container';
import { EmptyState } from '@/components/ui/EmptyState';
import { ScheduleCreationModal } from '@/components/calendar/ScheduleCreationModal';
import { ScheduleCard } from '@/components/schedules/ScheduleCard';
import { DeleteConfirmModal } from '@/components/shared/DeleteConfirmModal';
import { Calendar, Plus } from 'lucide-react';
import { ListSchedule } from '@/types/calendar';
import { calendarService } from '@/services/calendarService';
import { useListsStore } from '@/store/listsStore';
import { useNavigationStore } from '@/store/navigationStore';
import { AnimatedCard } from '@/components/ui/AnimatedCard';

export function Schedules() {
  const [schedules, setSchedules] = useState<ListSchedule[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ListSchedule | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ListSchedule | null>(null);
  const { lists, initializeLists } = useListsStore();
  const { setPageInfo } = useNavigationStore();

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  useEffect(() => {
    initializeLists();
    loadSchedules();
  }, [initializeLists]);

  // Set page info on mount
  useEffect(() => {
    setPageInfo('Schedules', {
      label: 'Create Schedule',
      icon: Plus,
      onClick: openCreateModal
    });
  }, [setPageInfo]);

  const loadSchedules = () => {
    const allSchedules = calendarService.getAllSchedules();
    setSchedules(allSchedules);
  };

  const handleToggleActive = (schedule: ListSchedule) => {
    calendarService.updateSchedule(schedule.id, { isActive: !schedule.isActive });
    loadSchedules();
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      calendarService.deleteSchedule(deleteConfirm.id);
      loadSchedules();
      setDeleteConfirm(null);
    }
  };

  const handleScheduleCreated = (_schedule: ListSchedule) => {
    loadSchedules();
    setShowCreateModal(false);
  };

  const getList = (listId: string) => lists.find(l => l.id === listId);

  return (
    <Container size="lg" className="py-6">
      {schedules.length === 0 ? (
        <AnimatedCard delay={0.1}>
          <EmptyState
            icon={<Calendar className="h-12 w-12 text-muted-foreground" />}
            title="No schedules yet"
            description="Create your first schedule to automate your recurring tasks."
            action={{
              label: "Create Schedule",
              onClick: openCreateModal
            }}
          />
        </AnimatedCard>
      ) : (
        <div className="grid gap-4">
          {schedules.map((schedule, index) => {
            const list = getList(schedule.listId);
            return (
              <AnimatedCard key={schedule.id} delay={index * 0.05}>
                <ScheduleCard
                  schedule={schedule}
                  list={list}
                  onEdit={(s) => setEditingSchedule(s)}
                  onDelete={(s) => setDeleteConfirm(s)}
                  onToggleActive={handleToggleActive}
                />
              </AnimatedCard>
            );
          })}
        </div>
      )}

      <ScheduleCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onScheduleCreated={handleScheduleCreated}
      />

      {editingSchedule && (
        <ScheduleCreationModal
          isOpen={!!editingSchedule}
          onClose={() => setEditingSchedule(null)}
          onScheduleCreated={(_schedule) => {
            loadSchedules();
            setEditingSchedule(null);
          }}
          initialListId={editingSchedule.listId}
          initialSchedule={editingSchedule}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        itemName={deleteConfirm?.title || getList(deleteConfirm?.listId || '')?.title || 'this schedule'}
        itemType="schedule"
        description="This will remove the schedule. The associated list will not be deleted."
      />
    </Container>
  );
}
